<template>
  <div
    id="video-item-container"
    :class="{ embedded: embed }"
    ref="videoContainer"
    style="position: relative; width: 100%"
  >
    <video-player
      ref="videoPlayer"
      @loadedmetadata="onMetadata"
      @ended="onEnded"
      @error="onError"
      @timeupdate="onTimeUpdate"
      @ready="onReady"
      @play="onPlay"
      class="w-100 h-100 vjs-default-skin vjs-big-play-centered vjs-show-big-play-button-on-pause"
      :options="playerOptions"
    >
    </video-player>
    <video-end-action :video-ended="ended" :item="item" />
  </div>
</template>

<script>
import iconMute from "./../../../images/icon-muted.svg";
import videoPlayer from "../video_player.vue";
import VideoEndAction from "./video_end_action";
import api from "../../api";

export default {
  name: "VideoContainer",
  props: {
    id: String,
    item: Object,
    poster: String,
    autoplay: Boolean,
    hasViewerDownloadButton: Boolean,
    isOwner: Boolean,
    host: String,
    statid: String,
    cta: Object,
    embed: Boolean,
  },
  data() {
    return {
      iconMute: iconMute,
      resetPosition: true,
      ended: false,
      pc_reported: 0,
      pc_increment: 0,
      playerOptions: {
        // videojs options
        muted: this.autoplay || false,
        autoplay: this.autoplay || false,
        language: "en",
        sources: [
          {
            src: this.item.links.content_url,
          },
        ],
        controls: true,
        liveui: true,
        errorDisplay: false,
        poster: this.poster,
        preload: "none", // a poster will stop "auto" preload
        aspectRatio: this.embed ? undefined : "16:9",
        fluid: !this.embed,
        fill: this.embed,
        playbackRates: [0.5, 1, 1.2, 1.5, 1.7, 2],
        controlBar: {
          pictureInPictureToggle: true,
          children: [
            "playToggle",
            "progressControl",
            "SeekToLive",
            "remainingTimeDisplay",
            "VolumeControl",
            "PlaybackRateMenuButton",
            "pictureInPictureToggle",
            "fullscreenToggle",
          ],
        },
        html5: {
          vhs: {
            nudgeMaxRetry: 5,
            maxLoadingDelay: 5,
            appendErrorMaxRetry: 5,
          },
        },
      },
    };
  },
  components: { videoPlayer, VideoEndAction },
  computed: {
    pcIncrement() {
      if (this.pc_increment) {
        return this.pc_increment;
      }
      let player = videojs("#video-item");
      if (!player?.duration()) {
        return NaN;
      }

      let min_interval = 3;
      let max_interval = 30;

      let interval = player.duration() * 0.05;
      let seconds = Math.min(Math.max(interval, min_interval), max_interval);

      return (this.pc_increment = Math.floor(
        (seconds / player.duration()) * 100
      ));
    },
  },
  methods: {
    zoomImage() {
      this.$parent.$parent.zoomImage();
    },
    setMinHeightWidth() {
      if (this.$parent.$parent.setMinHeightWidth) {
        this.$parent.$parent.setMinHeightWidth();
      }
    },
    updateItemStat: function (viewed_pc, length) {
      if (!this.isOwner) {
        let stat_id = this.statid;
        let params = {
          itemId: this.item.id,
          params: { stat_id, viewed_pc, length },
        };
        // embed is not using store, this is broken on embeds let's fix in another ticket.
        if (this.$store) {
          this.$store.dispatch("updateStats", params);
        }
      }
    },
    percentViewed: function () {
      let player = videojs("#video-item"); // There is probably a more Vue.js way to do this.

      if (isNaN(player.duration())) {
        return 0;
      }

      let timeRanges = player.played();
      let seconds_viewed = 0;

      for (let i = 0; i < timeRanges.length; ++i) {
        seconds_viewed += timeRanges.end(i) - timeRanges.start(i);
      }

      return (seconds_viewed / player.duration()) * 100;
    },
    onMetadata: function () {
      let player = videojs("#video-item"); // There is probably a more Vue.js way to do this.
      let iconMute = this.iconMute;

      // This is to start in-progress HLS streams at 0:00. Only call it once.
      if (this.resetPosition) {
        player.currentTime(0);
        this.resetPosition = false;
      }

      if (!this.embed && player.muted()) {
        var button = document.createElement("img");
        var playerContainer = this.$refs.videoContainer;

        button.addEventListener("click", function () {
          player.muted(false);
          playerContainer.removeChild(button);
        });

        // The VideoJS icons are here: https://videojs.github.io/font/
        // I'm not 100% sure why we made it into an SVG, but that's what the mute icon is now.
        // If needed, I think changing the tag to an <i> and adding this class should work too:
        //   button.classList.add("vjs-icon-volume-mute");
        button.setAttribute("src", iconMute);
        button.classList.add("icon-muted");
        playerContainer.appendChild(button);
      }
    },
    onTimeUpdate: function () {
      if (!this.isOwner) {
        let player = videojs("#video-item"); // There is probably a more Vue.js way to do this.
        let percentage = this.percentViewed();
        let next_increment = this.pc_reported + this.pcIncrement;
        let report_at_pc = next_increment > 100 ? 100 : next_increment;

        if (percentage > this.pc_reported && percentage >= report_at_pc) {
          // Round down to number divisible by pcIncrement
          let reported = percentage - (percentage % this.pcIncrement);
          this.pc_reported = percentage == 100 ? 100 : reported;
          this.updateItemStat(percentage, player.duration());
        }
      }
    },
    onReady: function () {
      const player = videojs("#video-item");
      if (this.embed && this.autoplay) {
        player.play();
        player.muted(false);
      }
    },
    onEnded: function () {
      this.ended = true;
      this.$emit("videoEnded", true);
    },
    onError: function (ev) {
      const player = videojs("#video-item");
      console.warn(`Player error: ${player.error()}`);

      let item_id = this.item.id;
      let error = player.error();
      let path = this.host + "/tracking/error";

      api.post(path, {
        event: "unplayable_video_viewed",
        item_id: item_id,
        embed: this.embed,
        error: {
          code: error.code,
          type: error.type,
          message: error.message,
        },
      });
    },
    onPlay: function (ev) {
      this.ended = false;
      this.$emit("videoEnded", false);
    },
  },
};
</script>

<style lang="scss">
.icon-muted {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: white;
  color: black;
  opacity: 0.8;
  bottom: 50px;
  right: 50px;
  position: absolute;
  -webkit-text-stroke: 1px black;
  -webkit-text-stroke-color: black;
}
</style>

<style scoped lang="scss">
@import "../../../packs/application.scss";

// This shouldn't have important, but we need to get rid of the id on #video-item-container
// first. A bunch of legacy code depends on it.
.embedded {
  width: 100% !important;
  height: 100% !important;
  max-width: unset !important;
  padding: 0 !important;
}

#video-item-container {
  flex: 1;
  width: auto;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0 auto;

  .video-player {
    width: 100%;
    height: 100%;
  }

  #video-item {
    background-color: transparent;
  }

  .vjs-poster {
    background-color: transparent;
  }
}

@media (max-width: 600px) {
  #video-item-container {
    height: auto;
  }
}
</style>
